import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {
    setGoogleMailSyncIsEnabled as setGoogleMailSyncIsEnabledMutation,
    setGoogleMailSyncIsEnabledVariables as setGoogleMailSyncIsEnabledMutationVariables,
    setOffice365MailSyncIsEnabled as setOffice365MailSyncIsEnabledMutation,
    setOffice365MailSyncIsEnabledVariables as setOffice365MailSyncIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import {authToService} from '../../../integrations/auth-to-service';
import {getAuthUrl} from '../../../integrations/integration-mutation-response-helpers';

import {updateCacheFromMutation} from '../../email-syncing/update-cache-from-mutation';
import SET_GOOGLE_MAIL_SYNC_IS_ENABLED from '../../graphql/mutations/set-google-mail-sync-is-enabled.graphql';
import SET_OFFICE_MAIL_SYNC_IS_ENABLED from '../../graphql/mutations/set-office365-mail-sync-is-enabled.graphql';

const officeWindowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 500,
} as const;

class GoogleSyncMutation extends Mutation<
    setGoogleMailSyncIsEnabledMutation,
    setGoogleMailSyncIsEnabledMutationVariables
> {}

class OfficeSyncMutation extends Mutation<
    setOffice365MailSyncIsEnabledMutation,
    setOffice365MailSyncIsEnabledMutationVariables
> {}

type Props = {
    provider: 'GOOGLE' | 'GOOGLE_APPS' | 'OFFICE365';
    fromDaysAgo?: number;
    createContacts?: boolean;
    children: (arg1: {
        startSync: () => Promise<any>;
        isMutationInFlight: boolean;
    }) => React.ReactElement;
};

/**
 * This is how we handle enabling initial sync for gmail or office365.
 * Office might need to pop an auth window with extra scopes, but google never will.
 */
export function SyncMutations(props: Props) {
    return (
        <OfficeSyncMutation
            mutation={SET_OFFICE_MAIL_SYNC_IS_ENABLED}
            update={updateCacheFromMutation}
            variables={{
                isEnabled: true,
                fromDaysAgo: props.fromDaysAgo,
                createContacts: props.createContacts,
            }}
        >
            {(enableO365, {loading: isOfficeMutationInFlight}) => (
                <GoogleSyncMutation
                    mutation={SET_GOOGLE_MAIL_SYNC_IS_ENABLED}
                    update={updateCacheFromMutation}
                    variables={{
                        isEnabled: true,
                        fromDaysAgo: props.fromDaysAgo,
                        createContacts: props.createContacts,
                    }}
                >
                    {(enableGoogle, {loading: isGoogleMutationInFlight}) => {
                        const isMutationInFlight =
                            isGoogleMutationInFlight || isOfficeMutationInFlight;

                        function startSync() {
                            if (props.provider === 'GOOGLE' || props.provider === 'GOOGLE_APPS') {
                                return enableGoogle();
                            }

                            if (props.provider === 'OFFICE365') {
                                return enableO365()
                                    .then(getAuthUrl)
                                    .then((authUrl) => {
                                        // If we get an authUrl back, then we need to auth
                                        if (authUrl) {
                                            return authToService({
                                                authUrl,
                                                windowFeatures: officeWindowFeatures,
                                                authCheckUrl: '/office365/has-auth',
                                            })
                                                .then(() => {
                                                    // TODO: determine if they actually enabled sync.
                                                    // Can't just check hasAuth, because it will always be
                                                    // true since they just set up sending.
                                                })
                                                .catch((errMessages) => {
                                                    return Promise.reject(errMessages);
                                                });
                                        }
                                    });
                            }

                            return Promise.reject('Invalid provider specified');
                        }

                        return props.children({startSync, isMutationInFlight});
                    }}
                </GoogleSyncMutation>
            )}
        </OfficeSyncMutation>
    );
}
